<template>
  <div class="system-ribbons" v-if="ribbons.length > 0">
    <transition-group name="ribbon-fade" tag="div">
      <div
        v-for="ribbon in ribbons"
        :key="ribbon.id"
        class="system-ribbon"
        :class="'ribbon-' + ribbon.type"
      >
        <div class="ribbon-content">
          <strong v-if="ribbon.title" class="ribbon-title">{{ ribbon.title }} —</strong>
          <span class="ribbon-text" v-html="ribbon.content"></span>
        </div>
        <button
          v-if="ribbon.is_dismissible"
          class="ribbon-close"
          @click="dismissRibbon(ribbon.id)"
          :aria-label="$t('common.close')"
        >
          ✕
        </button>
      </div>
    </transition-group>
  </div>
</template>

<script>
import apiClient from '@/apiClient';

export default {
  name: 'SystemRibbons',
  data() {
    return {
      ribbons: [],
      serverStatus: 'up',
    };
  },
  mounted() {
    this.fetchSystemInfo();
  },
  methods: {
    async fetchSystemInfo() {
      try {
        const response = await apiClient.get('/system/info/');
        const data = response.data;
        
        this.serverStatus = data.status;
        this.ribbons = data.messages || [];
      } catch (err) {
        console.warn('Could not fetch system info:', err.message);
      }
    },
    async dismissRibbon(messageId) {
      this.ribbons = this.ribbons.filter(r => r.id !== messageId);
      
      try {
        await apiClient.post(`/system/dismiss/${messageId}/`);
      } catch (err) {
        console.warn('Could not dismiss message:', err.message);
      }
    },
  },
};
</script>

<style scoped>
.system-ribbons {
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  z-index: 9000;
  display: flex;
  flex-direction: column;
}

.system-ribbon {
  display: flex;
  align-items: center;
  padding: 6px 20px;
  font-size: 0.8rem;
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  animation: slideDown 0.3s ease;
  letter-spacing: 0.02em;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.ribbon-content {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.ribbon-title {
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.ribbon-text {
  opacity: 0.92;
}

/* Links inside ribbons */
.ribbon-text :deep(a) {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  font-weight: 600;
  opacity: 0.95;
  transition: opacity 0.2s;
}
.ribbon-text :deep(a:hover) {
  opacity: 1;
}

.ribbon-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  opacity: 0.5;
  padding: 2px 6px;
  margin-left: 12px;
  border-radius: 3px;
  transition: opacity 0.2s, background 0.2s;
  flex-shrink: 0;
}

.ribbon-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

/* === Type-specific metallic gradients (iSamurai palette) === */

/* Warning: warm amber-bronze metallic */
.ribbon-warning {
  background: linear-gradient(135deg, #1a0f00 0%, #3d2200 20%, #6b3a00 50%, #3d2200 80%, #1a0f00 100%);
  color: #fbbf24;
  border-bottom-color: rgba(251, 191, 36, 0.15);
}

/* Info: deep cyan-purple metallic */
.ribbon-info {
  background: linear-gradient(135deg, #0a0a1a 0%, #0f1a2e 20%, #132040 50%, #0f1a2e 80%, #0a0a1a 100%);
  color: #13DADC;
  border-bottom-color: rgba(19, 218, 220, 0.15);
}

/* Error: deep crimson metallic */
.ribbon-error {
  background: linear-gradient(135deg, #1a0505 0%, #3d0a0a 20%, #6b1111 50%, #3d0a0a 80%, #1a0505 100%);
  color: #F091AF;
  border-bottom-color: rgba(240, 145, 175, 0.15);
}

/* Success: deep emerald metallic */
.ribbon-success {
  background: linear-gradient(135deg, #001a0a 0%, #003d1a 20%, #00552a 50%, #003d1a 80%, #001a0a 100%);
  color: #34d399;
  border-bottom-color: rgba(52, 211, 153, 0.15);
}

/* Maintenance: deep purple metallic (matches app accent) */
.ribbon-maintenance {
  background: linear-gradient(135deg, #100D20 0%, #1a1035 20%, #2d1a5e 50%, #1a1035 80%, #100D20 100%);
  color: #9264E3;
  border-bottom-color: rgba(146, 100, 227, 0.15);
}

/* Transitions */
.ribbon-fade-enter-active {
  transition: all 0.3s ease;
}
.ribbon-fade-leave-active {
  transition: all 0.2s ease;
}
.ribbon-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}
.ribbon-fade-leave-to {
  opacity: 0;
  transform: translateX(20px);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 600px) {
  .system-ribbon {
    font-size: 0.75rem;
    padding: 5px 10px;
  }
}
</style>
